import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import logger from "../utils/logger";

const AuthContext = createContext(null);
const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

// NOTE: Tokens stored in localStorage (not httpOnly cookies) because the frontend
// and backend are on different domains in this Kubernetes setup. Cross-origin
// httpOnly cookies require SameSite=None + Secure + matching domain configuration
// that isn't available with the current ingress. localStorage + Bearer header
// is the pragmatic choice here. XSS risk is mitigated by CSP headers.
function getToken() {
  return localStorage.getItem("flux_session_token");
}

function setToken(token) {
  localStorage.setItem("flux_session_token", token);
}

function clearToken() {
  localStorage.removeItem("flux_session_token");
}

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export { getAuthHeaders, API };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (err) {
      logger.error("Auth check failed:", err);
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData, sessionToken) => {
    setToken(sessionToken);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    const token = getToken();
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (err) {
      logger.error("Logout error:", err);
    }
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, logout, login, checkAuth }),
    [user, loading, logout, login, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
